import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Globaler Footer — sitewide interner Link-Hub (Hub-&-Spoke): jede Seite verlinkt
// die Säulen + Schlüsselseiten. Beschreibende Anker, keine "click here".
const Footer = () => {
  const { t, i18n } = useTranslation();
  const year = "2026";
  // Rechtstexte gibt es als statische Seiten auf Deutsch und Englisch. Wer die Seite
  // nicht auf Deutsch liest, bekommt die englische Fassung; die deutsche bleibt die
  // rechtlich maßgebliche (steht als Hinweis in der englischen Datei).
  const isDE = i18n.language?.startsWith("de");
  const privacyHref = isDE ? "/privacy" : "/en/privacy";
  const imprintHref = isDE ? "/impressum" : "/en/imprint";

  const leistungen = [
    { label: t("services.services.kiVertrieb.title"), to: "/ki-vertrieb" },
    { label: t("services.services.kiVersicherung.title"), to: "/ki-versicherung" },
    { label: t("services.services.dashboards.title"), to: "/management-dashboards" },
    { label: t("services.services.ai.title"), to: "/ai" },
    { label: t("services.services.strategy.title"), to: "/strategie" },
    { label: t("services.services.digital.title"), to: "/digital" },
  ];

  const mehr = [
    { label: t("navigation.insights"), to: "/insights" },
    { label: t("navigation.tools"), to: "/tools" },
    { label: t("navigation.buecher"), to: "/buecher" },
    { label: t("navigation.media"), to: "/#media" },
    { label: t("navigation.contact"), to: "/#contact" },
  ];

  const rechtliches = [
    // Impressum und Datenschutz sind statische HTML-Seiten ausserhalb der SPA.
    // Mit <Link> wuerde der React-Router eine abweichende SPA-Variante rendern
    // (beim Impressum standen dort bis 06.08.2026 sogar andere Kontaktdaten)
    // -> beide bewusst als harte Links.
    { label: t("footer.impressum"), staticHref: imprintHref },
    { label: t("footer.datenschutz"), staticHref: privacyHref },
    // Redaktions-/KI-Transparenzseite: statisch, bewusst nur deutsch (wie /data-deletion).
    // Label inline statt i18n-Key, damit die 8 Locale-Dateien unberuehrt bleiben.
    {
      label: isDE ? "Redaktion & KI-Einsatz" : "Editorial policy",
      staticHref: "/redaktion",
    },
    // Widerrufsweg: oeffnet das Cookie-Banner erneut (CookieBanner.jsx lauscht).
    {
      label: t("footer.cookieSettings"),
      action: () => window.dispatchEvent(new Event("open-cookie-banner")),
    },
  ];

  const Column = ({ heading, links }) => (
    <div>
      <h3 className="text-[#C5A66A] text-sm font-semibold uppercase tracking-wider mb-4">
        {heading}
      </h3>
      <ul className="space-y-2.5">
        {links.map((l) => {
          const linkClass =
            "text-gray-300 hover:text-white text-sm transition-colors duration-200";
          return (
            <li key={(l.staticHref || l.to || "action") + l.label}>
              {l.action ? (
                <button
                  type="button"
                  onClick={l.action}
                  className={linkClass + " text-left"}
                >
                  {l.label}
                </button>
              ) : l.staticHref ? (
                <a href={l.staticHref} className={linkClass}>
                  {l.label}
                </a>
              ) : (
                <Link to={l.to} className={linkClass}>
                  {l.label}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );

  return (
    <footer
      className="bg-[#0F1B2D] border-t border-white/10"
      role="contentinfo"
      aria-label="Footer"
    >
      <div className="container mx-auto px-6 max-w-6xl py-16">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center mb-4" aria-label="Zur Startseite">
              <div className="w-11 h-11 bg-gradient-to-br from-[#C5A66A] to-[#d4b886] rounded-lg flex items-center justify-center mr-3">
                <span className="text-[#0F1B2D] font-bold text-lg font-playfair">
                  DK
                </span>
              </div>
              <span className="font-playfair font-bold text-xl text-white">
                Daaniyal Khan
              </span>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed">
              {t("footer.tagline")}
            </p>
          </div>

          <Column heading={t("footer.colServices")} links={leistungen} />
          <Column heading={t("footer.colMore")} links={mehr} />
          <Column heading={t("footer.colLegal")} links={rechtliches} />
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 text-center md:text-left">
          <p className="text-gray-500 text-xs">
            © {year} Daaniyal Khan. {t("footer.rights")}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
